import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import {changePassword} from '../../redux/slices/auth';
import PrimaryAppBar from './PrimaryAppBar';
import SecondaryAppBar from './SecondayAppBar';
import {BluePillButton} from '../../common/components/BluePillButton';
import {PASSWORD_MIN_LENGTH} from '../../constants';

import baseStyles from '../../css/Base.module.css';

export default function ChangePassword() {
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [isNewPasswordVisible, setNewPasswordVisible] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [dialogMessage, setDialogMessage] = useState(null);

  const dispatch = useDispatch();

  function handlePasswordChanged(event) {
    setPassword(event.target.value);
  }
  function handleNewPasswordChanged(event) {
    setNewPassword(event.target.value);
  }

  function isSubmitButtonEnabled() {
    return password && newPassword;
  }

  function validateForm() {
    const errors = {};

    if (!password) errors.password = 'This field is required.';

    if (!newPassword) errors.newPassword = 'This field is required.';
    else if (newPassword.length < PASSWORD_MIN_LENGTH)
      errors.newPassword = `Use ${PASSWORD_MIN_LENGTH} or more characters with a mix of uppercase letters, lowercase letters, and numbers.`;

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  }

  async function handleSubmitForm(event) {
    event.preventDefault();
    const isFormValid = validateForm();

    if (isFormValid) {
      let response;

      try {
        response = await dispatch(changePassword(password, newPassword));
      } catch (error) {
        console.log(`change password error: ${JSON.stringify(error)}`);

        const {code, message} = error;
        if (code === 'NotAuthorizedException') {
          setDialogMessage('Incorrect password. Try again.');
        } else if (
          code === 'InvalidParameterException' &&
          message.includes("Value at 'previousPassword' failed to satisfy constraint")
        ) {
          setDialogMessage('Incorrect password.');
        } else if (code === 'InvalidPasswordException') {
          setDialogMessage(
            `Use ${PASSWORD_MIN_LENGTH} or more characters with a mix of uppercase letters, lowercase letters, and numbers.`,
          );
        } else if (code === 'LimitExceededException') {
          setDialogMessage('Attempt limit exceeded. Try again later.');
        } else {
          setDialogMessage('An error occurred, try again.');
        }
      }

      console.log(`change password response: ${JSON.stringify(response)}`);

      if (response === 'SUCCESS') {
        setDialogMessage('Your password has been changed.');
        setPassword('');
        setNewPassword('');
      }
    }
  }

  const handleClickShowPassword = () => {
    setPasswordVisible(!isPasswordVisible);
  };
  const handleClickShowNewPassword = () => {
    setNewPasswordVisible(!isNewPasswordVisible);
  };
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  function renderDialog() {
    return (
      <Dialog open={!!dialogMessage} onClose={() => setDialogMessage(null)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMessage(null)} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <PrimaryAppBar />
      <SecondaryAppBar title="Change Password"></SecondaryAppBar>

      {renderDialog()}

      <form className="p-20" onSubmit={handleSubmitForm}>
        <Grid container spacing={3}>
          <Grid item xl={2} lg={4} md={6} sm={8} xs={12}>
            <FormControl fullWidth required>
              <InputLabel htmlFor="login-form-password" variant="standard">
                Current password
              </InputLabel>
              <Input
                id="login-form-password"
                className={baseStyles['m-t-10']}
                value={password}
                onChange={handlePasswordChanged}
                type={isPasswordVisible ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} size="large">
                      {isPasswordVisible ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
                error={!!formErrors.password}
                autoFocus
              />
              {!!formErrors.password && <FormHelperText error>{formErrors.password}</FormHelperText>}
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xl={2} lg={4} md={6} sm={8} xs={12}>
            <FormControl fullWidth required>
              <InputLabel htmlFor="login-form-new-password" variant="standard">
                New password
              </InputLabel>
              <Input
                id="login-form-new-password"
                className={baseStyles['m-t-10']}
                value={newPassword}
                onChange={handleNewPasswordChanged}
                type={isNewPasswordVisible ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowNewPassword} onMouseDown={handleMouseDownPassword} size="large">
                      {isNewPasswordVisible ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
                error={!!formErrors.newPassword}
              />
              {!!formErrors.newPassword ? (
                <FormHelperText error variant="standard">
                  {formErrors.newPassword}
                </FormHelperText>
              ) : (
                <FormHelperText variant="standard">{`Use ${PASSWORD_MIN_LENGTH} or more characters with a mix of uppercase letters, lowercase letters, and numbers.`}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={3} className="m-t-10">
          <Grid item xl={2} lg={4} md={6} sm={8} xs={12}>
            <Grid item className={baseStyles['text-align-end']}>
              <BluePillButton type="submit" variant="outlined" disabled={!isSubmitButtonEnabled()}>
                SUBMIT
              </BluePillButton>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
